/**
 * RaceTrace Live, at the top of the page: laps since the last green (or under this caution) and
 * laps to go.
 */
import type { RaceChartData } from "@shared/types";

/**
 * Laps of the current green run: the leader's laps since the last caution ended (the chart's FCY
 * bands, in leader laps), or since the start. Under a caution: the laps it has run so far.
 */
export function greenRun(data: RaceChartData, leaderLap: number, caution: boolean): { caution: boolean; laps: number } {
  const bands = [...(data.fcy ?? [])].filter(([a]) => a <= leaderLap).sort((x, y) => x[0] - y[0]);
  const last = bands[bands.length - 1];
  if (caution) return { caution: true, laps: last ? Math.max(1, leaderLap - last[0] + 1) : 1 };
  // A band still open at the leader's lap is a caution just ended: the green run starts after it.
  return { caution: false, laps: Math.max(0, leaderLap - (last ? Math.min(last[1], leaderLap) : 0)) };
}

type Car = RaceChartData["cars"][string];

/** The overall leader's chart key: position 1, else the most laps and the earliest to complete them. */
export function overallLeaderKey(data: RaceChartData): string | null {
  const lead = overallLeader(data);
  return lead ? (Object.keys(data.cars).find((k) => data.cars[k] === lead) ?? null) : null;
}

/** The overall leader: position 1, else the most laps and the earliest to complete them. */
function overallLeader(data: RaceChartData): Car | null {
  const cars = Object.values(data.cars).filter((c) => c.laps.length > 0);
  const first = cars.find((c) => c.finishPos === 1);
  if (first) return first;
  let best: { c: Car; l: number; t: number } | null = null;
  for (const c of cars) {
    const l = c.laps.length;
    const t = c.laps.reduce((a, x) => a + (x.ltSec > 0 ? x.ltSec : 0), 0);
    if (!best || l > best.l || (l === best.l && t < best.t)) best = { c, l, t };
  }
  return best?.c ?? null;
}

/**
 * Laps to go, and the lap that starts after the clock runs out. With the overall leader's green
 * pace (`paceS`): the time still to run, less the time its stops still to make will take
 * (`stopS`), at that pace (live-strategy raceLapsToGo works both out). The laps to come are taken
 * as green: a caution means fewer, so for fuel this is the safe side (Sunday 7 Hour 2026: right at
 * every lap of the last stint, where the average lap below was one short until 10 to go).
 * Without a pace yet: the time to go at the leader's average lap so far (stops and cautions
 * included). Null without a time to go or a leader.
 */
export function lapsToGo(data: RaceChartData, timeToGoS: number | null, opts: { paceS?: number | null; stopS?: number } = {}): number | null {
  if (timeToGoS === null) return null;
  const lead = overallLeader(data);
  if (!lead || !lead.laps.length) return null;
  if (timeToGoS <= 0) return 0;
  if (opts.paceS && opts.paceS > 0) return Math.ceil(Math.max(0, timeToGoS - (opts.stopS ?? 0)) / opts.paceS);
  const total = lead.laps.reduce((a, l) => a + (l.ltSec > 0 ? l.ltSec : 0), 0);
  const avg = total / lead.laps.length;
  if (!(avg > 0)) return null;
  return Math.ceil(timeToGoS / avg);
}

/**
 * The laps each car still has to run to the flag, by chart key: the flag falls when the overall
 * leader finishes, so every car runs the leader's laps to go (`togo`: lapsToGo), plus one for each lap the
 * leader has completed since the car last crossed the line (a car on the lead lap that has not yet
 * crossed has one more to run). A car that holds its place runs these; a lap given up is a lap lost
 * to the leader. The times are each car's summed lap times (the chart's own clock). Null without
 * laps to go.
 *
 * A car slower than the leader runs fewer: with `paces` (green s/lap: the leader's, and a car's by
 * chart key), it runs the laps it crosses at its own pace until the leader takes the flag (the
 * leader's last crossing plus its laps to go at its pace, and never before the clock runs out:
 * the latest crossing plus `timeToGoS`), never more than the count above. Cautions to come slow
 * every car alike, so the ratio of the paces holds where the paces alone would not. Back-tested
 * on WRL Barber and Utah Saturday 8 Hour 2026, 2-4 h to go: GP2 +21 and +10 laps too many
 * before, +11 and +1 with it. A car without a pace, or without the leader's, keeps the count above.
 */
export function lapsLeftByCar(
  data: RaceChartData,
  togo: number | null,
  paces?: { leadS: number | null; byCar: ReadonlyMap<number, number | null>; timeToGoS?: number | null },
): Map<number, number> | null {
  const lead = overallLeader(data);
  if (togo === null || !lead) return null;
  // The leader's summed time at each lap it completed.
  const at: number[] = [];
  let t = 0;
  for (const l of lead.laps) at.push((t += l.ltSec > 0 ? l.ltSec : 0));
  // When the leader takes the flag, on the same clock: its laps to go at its pace, which leave out
  // the time of the stops it still owes, so never before the clock runs out (the latest crossing
  // in the race plus the time to go).
  const leadS = paces?.leadS;
  const ttg = paces?.timeToGoS;
  let now = 0;
  if (ttg != null) for (const c of Object.values(data.cars)) now = Math.max(now, c.laps.reduce((a, x) => a + (x.ltSec > 0 ? x.ltSec : 0), 0));
  const flagT = leadS && leadS > 0 ? Math.max(t + togo * leadS, ttg != null ? now + ttg : 0) : null;
  const out = new Map<number, number>();
  for (const [k, c] of Object.entries(data.cars)) {
    if (!c.laps.length) continue;
    const ct = c.laps.reduce((a, x) => a + (x.ltSec > 0 ? x.ltSec : 0), 0);
    // The leader's crossings after the car's last one.
    let since = 0;
    for (let i = at.length - 1; i >= 0 && at[i] > ct; i--) since++;
    const most = c === lead ? togo : togo + since;
    const pace = paces?.byCar.get(Number(k));
    const own = c !== lead && flagT !== null && pace && pace > 0 ? Math.max(0, Math.ceil((flagT - ct) / pace - 1e-9)) : most;
    out.set(Number(k), Math.min(most, own));
  }
  return out;
}

/** The class leader's laps averaged for the virtual car. */
export const VIRTUAL_LAPS = 3;

/**
 * The virtual car of each class: a car lapping at its class leader's average over its last
 * VIRTUAL_LAPS racing laps (no in- or out-laps), placed so it crosses the line exactly as the race
 * clock runs out. Where it is now (metres past the line) is the time to go at that pace, counted
 * back from the line: the class leader ahead of it on the road has one more lap to run than it.
 */
export function virtualCars(data: RaceChartData, timeToGoS: number, len: number, skip: Set<number> = new Set()): { cls: string; d: number; pace: number }[] {
  if (!(timeToGoS > 0) || !(len > 0)) return [];
  const out: { cls: string; d: number; pace: number }[] = [];
  for (const [k, c] of Object.entries(data.cars)) {
    if (c.finishPosClass !== 1 || skip.has(Number(k))) continue;
    const xs: number[] = [];
    for (let i = c.laps.length - 1; i > 0 && xs.length < VIRTUAL_LAPS; i--) {
      const l = c.laps[i];
      if (l.pit === 0 && c.laps[i - 1].pit === 0 && l.ltSec > 0) xs.push(l.ltSec);
    }
    if (xs.length < VIRTUAL_LAPS) continue;
    const pace = xs.reduce((a, b) => a + b, 0) / xs.length;
    const back = ((timeToGoS / pace) * len) % len;
    out.push({ cls: c.cls, d: (len - back) % len, pace });
  }
  return out.sort((a, b) => a.cls.localeCompare(b.cls));
}
